import React from 'react'
import { cn } from '../../../lib/utils'
import MeetingInfo from './MeetingInfo'
import {
  PhoneIcon,
  DocumentTextIcon,
  BriefcaseIcon,
  RocketLaunchIcon,
  ChatBubbleLeftRightIcon,
  CurrencyDollarIcon,
  ScaleIcon,
  TrophyIcon,
  UserGroupIcon,
  PencilIcon,
  ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/outline'
import { Gym, UserStar, Network } from 'iconoir-react'
import { LinkedInIcon, TargetIcon } from './icons'
import { useBenefits } from '../BenefitsProvider'
import { useTranslation } from '~/hooks/useTranslation'
import moment from 'moment'
import CalendlyEventInfo from './CalendlyEventInfo'
import UpcomingMeetings from './UpcomingMeetings'
import { Button } from '~/lib/ui/button'
import { CoachServicesCircle } from '~/components/icons/YnsIcon'
import { useIsDesktop } from '~/hooks/useMediaQuery'

const icons = {
  phone: PhoneIcon,
  document: DocumentTextIcon,
  linkedin: LinkedInIcon,
  briefcase: BriefcaseIcon,
  target: TargetIcon,
  rocket: RocketLaunchIcon,
  people: UserGroupIcon,
  chat: ChatBubbleLeftRightIcon,
  dollar: CurrencyDollarIcon,
  wellness: ScaleIcon,
  retirement: TrophyIcon,
  recruiting: UserStar,
  gym: Gym,
  network: Network,
  pencil: PencilIcon,
  interview: ChatBubbleBottomCenterTextIcon,
  liaison: CoachServicesCircle,
  // Add custom icons manually as needed
}

export default function ServiceCard({
  requestType,
  title,
  description,
  buttonText,
  iconType,
  fullWidth,
  photoUrl,
  coachCard,
  classes,
  liaison,
  buttonUrl,
  ...props
}) {
  const IconComponent = icons[iconType]

  const { t } = useTranslation('service_card')

  const isDesktop = useIsDesktop()

  const { serviceRequestList, select } = useBenefits()

  const calendlyEvent = serviceRequestList[requestType]?.calendly_event
  const requestComplete = serviceRequestList[requestType]?.completed
  const meetingViaCalendly =
    serviceRequestList[requestType]?.meeting_via_calendly

  const whiteText = select && classes !== 'bg-intoo-blue-light'

  const textColor = whiteText ? 'text-white' : ''

  const iconBg = select ? 'bg-white' : 'bg-intoo-green-coachcta'
  const introCallScheduled =
    select &&
    requestType === 'ServiceRequest::OrientationCall' &&
    calendlyEvent &&
    calendlyEvent['start_time']

  const titleText =
    introCallScheduled && requestComplete
      ? t('coach_introduction.meet_with_coach')
      : title

  return (
    <Wrapper
      eventName="Meeting"
      calendlyEvent={calendlyEvent}
      introCallScheduled={introCallScheduled}
      className={cn(
        'flex flex-col rounded-2xl',
        classes,
        !liaison && 'p-5',
        fullWidth && 'col-span-1 md:col-span-2',
      )}
      requestComplete={requestComplete}
      {...props}
    >
      <div
        className={cn(
          'flex flex-col items-center justify-between gap-6 md:flex-row',
          liaison && 'xl:min-h-[13rem]',
        )}
      >
        <div className={cn('service-card flex-1', textColor, liaison && 'p-5')}>
          <div className={cn('flex items-center gap-3', textColor)}>
            <div className={cn('rounded-full p-3', iconBg)}>
              {IconComponent && (
                <IconComponent
                  strokecolor={'currentColor'}
                  className={cn(
                    'h-5 w-5',
                    select ? 'text-black' : 'text-white',
                  )}
                />
              )}
            </div>
            <div className="subtitle !text-xl">{titleText}</div>
            {introCallScheduled && !requestComplete && (
              <CallScheduled calendlyEvent={calendlyEvent} />
            )}
          </div>
          <p className="mt-2 flex-grow">{description}</p>

          <div className="mt-3">
            {calendlyEvent ? (
              <MeetingInfo
                calendlyEvent={calendlyEvent}
                requestType={requestType}
                buttonText={buttonText}
                requestComplete={requestComplete}
                eventName="Meeting"
                cardTitle={title}
                meetingViaCalendly={meetingViaCalendly}
                introCallScheduled={introCallScheduled}
                liaison={liaison}
                buttonUrl={buttonUrl}
                {...props}
              />
            ) : (
              <Button variant="coachcta">
                <a
                  className="text-white hover:text-white focus:text-white"
                  href={buttonUrl}
                >
                  {buttonText}
                </a>
              </Button>
            )}
          </div>
        </div>
        {photoUrl && !liaison && (
          <div
            style={{ backgroundImage: `url(${photoUrl})` }}
            className={`bg-contain bg-center bg-no-repeat ${
              coachCard
                ? 'w-[300px] md:-mt-[30px] md:h-[130%]'
                : 'h-full w-60 object-cover'
            }`}
          />
        )}
        {liaison && isDesktop && (
          <div className="w-0 lg:w-[15rem]">
            <img src={photoUrl} alt="" className="rounded-r-[20px]" />
          </div>
        )}
      </div>
    </Wrapper>
  )
}

const CallScheduled = ({ calendlyEvent }) => {
  const { t } = useTranslation('service_card')
  const { start_time } = calendlyEvent
  const date = moment(start_time).format('MM/DD/YY')
  return (
    <div className="rounded-[5px] bg-intoo-blue-bright p-1 text-white">
      {t('call_scheduled', { date })}
    </div>
  )
}

const Wrapper = ({
  className,
  introCallScheduled,
  requestComplete,
  children,
  ...props
}) => {
  const { setPage, setShowScheduleDialog, setDialogTitle } = useBenefits()
  const { t } = useTranslation('schedule_modal')
  const handleScheduleCallClick = () => {
    setDialogTitle(t('cta'))
    setPage(0)
    setShowScheduleDialog(true)
  }
  if (introCallScheduled) {
    return (
      <div className={className}>
        {children}
        <div>
          {requestComplete ? (
            <Button variant={'outline'} onClick={handleScheduleCallClick}>
              {t('cta')}
            </Button>
          ) : (
            <CalendlyEventInfo
              {...props}
              introCallScheduled={introCallScheduled}
            />
          )}

          <UpcomingMeetings />
        </div>
      </div>
    )
  }

  return <div className={className}>{children}</div>
}
